<script setup lang="ts">
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';
import { IonButton, IonContent, IonIcon, IonLabel, IonModal, IonSegment, IonSegmentButton, IonSpinner } from '@ionic/vue';
import { TopUp } from '@/enums/values.enum';
import { arrowForwardOutline, chevronBackOutline, chevronDown as chevronDownIcon, chevronForwardOutline, closeOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import { TransactionType, TransactionSubtype } from '@/enums/wallet.enum';
import { useSharedStore } from '@/stores/shared/sharedStore';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import { WalletPackage } from '@/shared/interfaces/wallet';
import hexRgb from 'hex-rgb';
import ItemWithContentStandalone from '@/shared/components/ui/ItemWithContentStandalone.vue';
import rgbHex from 'rgb-hex';
import searchUser from '@/modules/guest/composables/useGuestUser';
import TopBar from '@/shared/components/TopBar.vue';
import type { Transaction } from '@/shared/interfaces/transaction';
import useRfidGuest from '@/modules/rfid/composables/useRfidGuest';
import useTenant from '@/shared/composables/useTenant';
import useTopUp from '@/modules/top-up/composables/useTopUp';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import useCurrencies from '@/shared/composables/useCurrencies';
import CurrencyInput from '@/shared/components/sign-up-forms/CurrencyInput.vue';
import CountrySelect from '@/shared/components/countries/CountrySelect.vue';
import useStorageService from '@/shared/composables/useStorageService';
import type { PreferredPaymentMode } from '@/modules/top-up/composables/useTopUp';

// Init
const { t } = useI18n();
const route = useRoute();
const encryptedRfidTag = route.query.id?.toString();
const loginToken = route.query.loginToken?.toString() || '';
const walletId = route.params.wallet_id?.toString();
const backToDashboard = route.query.backToDashboard?.toString();
const transactionStore = useTransactionStore();
const { setItemsAndTotal } = transactionStore;
const { getPackages, getMinimumTopUpAmount, getPreviousTopUpAmount, selectedWallet } = useWallet();
const effectiveWalletId = computed(() => selectedWallet.value?.id?.toString() || walletId);
const { minimumTopUp } = getMinimumTopUpAmount(effectiveWalletId);
const { previousTopUp } = getPreviousTopUpAmount(effectiveWalletId, encryptedRfidTag || loginToken ? ref(false) : ref(true));
const { walletPackages } = getPackages(effectiveWalletId);
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { calculateShade, convertToCurrency, replaceTopUpGeneralMessageVariables } = useUtils();
const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(calculateShade(primaryRgb.toString(), 0.2));
const selectedWalletPackage = ref();
const showClickToPayInfoModal = ref(false);
const { getWalletBalance } = useRfidGuest();
const { refetchWalletBalanceData, walletBalanceData } = getWalletBalance(encryptedRfidTag);
const { fiatCurrency } = useCurrencies();
const { get, set } = useStorageService();

const { availablePaymentCountries, checkIsLatestAmountGreaterThanMinimumAmount, enableTopUpSubmitButton, formValue, minimumAmountError, onAmountChange, paymentMethodCountry, switchPreferredPaymentMode, countrySelectionValidated, getApplicablePaymentCountry, startTopUp, selectTopUp } = useTopUp(tenantConfig, minimumTopUp);
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const sharedStore = useSharedStore();
const { getWallets } = useWallet();
const { wallets } = getWallets();
const { guestUserProfile, userMutate } = searchUser();
const topUpType = ref('');
const topupableWallets = computed(() => wallets.value?.filter((w) => w.currency.topupable) || []);
const configuredPreferredPaymentMode = computed<PreferredPaymentMode>(() => {
	if (tenantConfig.value?.viva_wallet_click_to_pay_preferred_mode) return 'click_to_pay';
	if (tenantConfig.value?.viva_wallet_payconiq_preferred_mode) return 'payconiq';
	return null;
});
const activePreferredPaymentMode = computed<PreferredPaymentMode>(() => (sharedStore.shouldUsePreferredPaymentMode ? configuredPreferredPaymentMode.value : null));
const isPayconiqPreferredModeActive = computed(() => activePreferredPaymentMode.value === 'payconiq');
const isClickToPayPreferredModeActive = computed(() => activePreferredPaymentMode.value === 'click_to_pay');
const isPreferredPaymentModeActive = computed(() => !!activePreferredPaymentMode.value);
const shouldSkipOverviewForPreferredPayment = computed(() => {
	const topUpCost = Number(tenantConfig.value?.top_up_cost) || 0;
	const cardCreationCost = Number(tenantConfig.value?.top_up_card_creation_cost) || 0;

	return isPreferredPaymentModeActive.value && topUpCost === 0 && (!tenantConfig.value?.top_up_card_creation_cost_to_customer || cardCreationCost === 0);
});

watch(
	[guestUserProfile],
	() => {
		if (loginToken && guestUserProfile.value) {
			selectedWallet.value = guestUserProfile.value?.wallets.find((wallet) => wallet.id === +walletId);
		}
	},
	{ immediate: true },
);

onMounted(async () => {
	window.addEventListener('keydown', handleEnterKey);
	window.addEventListener('click', closeWalletSelectOnOutsideClick);
	if (loginToken) {
		userMutate({ login_token: loginToken });
	}

	const autoContinue = route.query.autoContinue === 'true';

	// Reset state - only set preferred mode if not coming from quick topup
	if (!autoContinue) {
		sharedStore.shouldUsePayconiq = true;
		sharedStore.shouldUsePreferredPaymentMode = true;
	}

	if (tenantConfig && tenantConfig.value && tenantConfig.value.wallet_enable_packages) {
		topUpType.value = TopUp.TopUpTypePackages;
	} else {
		topUpType.value = TopUp.TopUpTypeFreeAmount;
	}

	const queryPackageId = route.query.packageId ? +route.query.packageId : 0;

	watch(
		() => [walletPackages.value, queryPackageId],
		([packages, pkgId]) => {
			if (pkgId && packages && packages.length > 0) {
				const found = packages.find((p: WalletPackage) => p.id === pkgId);
				if (found && (!selectedWalletPackage.value || selectedWalletPackage.value.id !== found.id)) {
					console.log('[TopUpPage] Auto-selecting package from query param:', found.name, found.id);
					selectPackage(found);
					if (autoContinue) {
						console.log('[TopUpPage] autoContinue=true, will call addTopUpPackage after nextTick');
						setTimeout(() => {
							console.log('[TopUpPage] autoContinue executing addTopUpPackage');
							console.log('[TopUpPage] State before addTopUpPackage:', {
								selectedWalletPackage: selectedWalletPackage.value?.id,
								shouldSkipOverview: shouldSkipOverviewForPreferredPayment.value,
								activePreferredPaymentMode: activePreferredPaymentMode.value,
								sharedStore_shouldUsePreferredPaymentMode: sharedStore.shouldUsePreferredPaymentMode,
								sharedStore_shouldUsePayconiq: sharedStore.shouldUsePayconiq,
							});
							addTopUpPackage();
						}, 500);
					}
				}
			}
		},
		{ immediate: true },
	);

	if (encryptedRfidTag) {
		await refetchWalletBalanceData();
		selectedWallet.value = walletBalanceData.value?.wallets.find((wallet) => wallet.id === +walletId);
	} else if (loginToken) {
		// use mutation to get user profile
	} else {
		if (wallets.value?.length) {
			if (walletId) {
				selectedWallet.value = wallets.value.find((w) => w.id.toString() === walletId);
			}
			if (!selectedWallet.value) {
				selectedWallet.value = wallets.value.find((w) => w.currency.topupable && w.currency.default) || wallets.value.find((w) => w.currency.topupable) || wallets.value[0];
			}
		}
	}
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleEnterKey);
	window.removeEventListener('click', closeWalletSelectOnOutsideClick);
});

const closeWalletSelectOnOutsideClick = (e: MouseEvent) => {
	const path = e.composedPath?.() || [];
	if (!path.some((el: any) => el?.id === 'wallet-select-dropdown') && isWalletSelectOpen.value) {
		isWalletSelectOpen.value = false;
	}
};

const isWalletSelectOpen = ref(false);

const toggleWalletSelect = () => {
	isWalletSelectOpen.value = !isWalletSelectOpen.value;
};

const onWalletSelect = (newWalletId: string) => {
	const wallet = wallets.value?.find((w) => w.id.toString() === newWalletId);
	if (wallet) {
		selectedWallet.value = wallet;
	}
	isWalletSelectOpen.value = false;
};

watch(selectedWallet, () => {
	formValue.value = '0,00';
	selectedWalletPackage.value = undefined;
	enableTopUpSubmitButton.value = false;
	minimumAmountError.value = false;
});

// selectPackage
const selectPackage = (walletPackage: WalletPackage) => {
	if (selectedWalletPackage.value === undefined || selectedWalletPackage.value !== walletPackage) {
		const walletItems: any = [
			{
				description: `${walletPackage.name}`,
				currency: '€',
				itemPrice: +walletPackage.clientx_price,
				quantity: 1,
				hideQuantiy: true,
				hidePrice: false,
			},
		];

		let totalPriceString = '';
		let totalPriceNumber = 0;

		// Include administrative fee if applicable
		if (walletPackage.clientx_price_incl_fee > +walletPackage.clientx_price) {
			const fee = walletPackage.clientx_price_incl_fee - +walletPackage.clientx_price;
			walletItems.push({
				description: t('transaction.administrative_fee'),
				currency: '€',
				itemPrice: fee,
				quantity: 1,
				hideQuantiy: true,
				hidePrice: false,
			});
			totalPriceNumber = walletPackage.clientx_price_incl_fee;
		} else {
			totalPriceNumber = +walletPackage.clientx_price;
		}

		totalPriceString = `€${totalPriceNumber.toFixed(2)}`;

		const transaction: Transaction = {
			additionalInfo: {
				isGuestTopUp: !!(encryptedRfidTag || loginToken),
				encryptedRfidTag,
				redirectPath: encryptedRfidTag ? `/wallet/wallet-handler/${encryptedRfidTag}` : '/dashboard',
				packageContents: walletPackage.contents,
			},
			items: [...walletItems],
			totalDescription: `${t('transaction.total')}`,
			totalPriceString,
			totalPriceNumber,
			totalPriceWithoutFee: +walletPackage.clientx_price,
			type: TransactionType.TOP_UP,
			subtype: TransactionSubtype.PACKAGE,
		};

		setItemsAndTotal(transaction);
		selectedWalletPackage.value = walletPackage;
	} else {
		selectedWalletPackage.value = undefined;
	}
};

const goToTopUpOverview = () => {
	router.push({
		name: 'top-up-overview',
		params: { wallet_id: effectiveWalletId.value },
		query: {
			id: encryptedRfidTag,
			loginToken,
			backToDashboard,
			packageId: selectedWalletPackage.value?.id,
			amount: formValue.value,
			topUpType: topUpType.value,
		},
	});
};

const addTopUp = async () => {
	const amountDecimal = parseFloat(formValue.value.replace(',', '.'));
	const tokenAmount = selectedWallet.value?.currency.name === 'EUR' ? amountDecimal : +(amountDecimal / +selectedWallet.value.currency.exchange_rate).toFixed(2);
	const totalAmountWithoutFee = tokenAmount;

	if (topUpType.value === TopUp.TopUpTypeFreeAmount && amountDecimal > 0 && selectedWallet.value?.currency) {
		const tokenDisplay = selectedWallet.value.currency.name === 'EUR'
			? amountDecimal.toFixed(2)
			: (amountDecimal / +selectedWallet.value.currency.exchange_rate).toFixed(2);

		const items = [
			{
				description: `${selectedWallet.value.currency.prefix} ${tokenDisplay} ${selectedWallet.value.currency.name}`,
				itemPrice: +amountDecimal,
				quantity: 1,
				currency: '€',
			},
		];
		if (+tenantConfig.value!.top_up_cost > 0) {
			items.push({
				description: t('transaction.administrative_fee'),
				itemPrice: +tenantConfig.value!.top_up_cost,
				quantity: 1,
				currency: '€',
			});
		}

		const topUpAmountWithTopUpCost = +amountDecimal + (tenantConfig.value?.top_up_cost ? +tenantConfig.value.top_up_cost : 0);

		const tokenTitle = selectedWallet.value.currency.name === 'EUR'
			? `€ ${amountDecimal}`
			: `${selectedWallet.value.currency.prefix} ${(amountDecimal / +selectedWallet.value.currency.exchange_rate).toFixed(2)} ${selectedWallet.value.currency.name}`;

		const transaction: Transaction = {
			additionalInfo: {
				isGuestTopUp: !!(encryptedRfidTag || loginToken),
				encryptedRfidTag,
				redirectPath: encryptedRfidTag ? `/wallet/wallet-handler/${encryptedRfidTag}` : '/dashboard',
			},
			title: tokenTitle,
			items,
			totalDescription: t('transaction.total'),
			totalPriceString: `€${topUpAmountWithTopUpCost.toFixed(2)}`,
			totalPriceNumber: topUpAmountWithTopUpCost,
			totalPriceWithoutFee: totalAmountWithoutFee,
			type: TransactionType.TOP_UP,
			subtype: TransactionSubtype.FREE_AMOUNT,
			walletId: effectiveWalletId.value,
		};

		setItemsAndTotal(transaction);

		// Check if we should skip overview and go directly to checkout
		const shouldSkipOverview = shouldSkipOverviewForPreferredPayment.value;

		console.log('Skip overview conditions:', {
			preferredPaymentMode: activePreferredPaymentMode.value,
			shouldUsePreferredPaymentMode: sharedStore.shouldUsePreferredPaymentMode,
			topUpCost: tenantConfig.value?.top_up_cost,
			cardCreationCostToCustomer: tenantConfig.value?.top_up_card_creation_cost_to_customer,
			cardCreationCost: tenantConfig.value?.top_up_card_creation_cost,
			shouldSkip: shouldSkipOverview,
		});

		if (shouldSkipOverview) {
			// Check if minimum amount data is loaded, if not fall back to overview
			if (!minimumTopUp.value || minimumTopUp.value.amount === undefined) {
				console.log('Minimum top-up data not loaded yet, going to overview');
				goToTopUpOverview();
				return;
			}

			const minimumLocalAmount = minimumTopUp.value.amount;
			const topUpCost = Number(tenantConfig.value.top_up_cost) || 0;
			const totalAmount = Number((amountDecimal + topUpCost).toFixed(2));

			let respectsMinimums = false;
			if (minimumLocalAmount > 0) {
				respectsMinimums = amountDecimal >= minimumLocalAmount && totalAmount >= 1;
			} else {
				respectsMinimums = totalAmount >= 1;
			}

			console.log('Validation result:', { respectsMinimums });

			if (!respectsMinimums) {
				// Don't skip if minimum requirements not met, go to overview to show error
				console.log('Minimum amount validation failed, going to overview');
				goToTopUpOverview();
				return;
			}

			// The formValue should remain as the user's input amount (local currency format)
			// startTopUp will handle the conversion and formatting for the API

			// Start the top-up directly
			const paymentMethodCountry = getApplicablePaymentCountry();

			if (!selectedWallet.value) {
				selectedWallet.value = await get('selectedWallet');
			}

			startTopUp(paymentMethodCountry, activePreferredPaymentMode.value, encryptedRfidTag, effectiveWalletId.value, selectedWallet.value?.currency.id, loginToken);
			return;
		}
	}

	goToTopUpOverview();
};

const addTopUpPackage = () => {
	// Check if we should skip overview and go directly to checkout
	const shouldSkipOverview = shouldSkipOverviewForPreferredPayment.value;

	if (shouldSkipOverview && selectedWalletPackage.value) {
		// Start the package top-up directly
		const paymentMethodCountry = getApplicablePaymentCountry();

		selectTopUp(selectedWalletPackage.value.id, paymentMethodCountry, activePreferredPaymentMode.value, encryptedRfidTag, loginToken);
		return;
	}

	goToTopUpOverview();
};

// Go back
const goBack = () => {
	if (backToDashboard === 'true') {
		router.push('/dashboard');
	} else {
		router.back();
	}
};

const handleEnterKey = (event: KeyboardEvent) => {
	if (event.key === 'Enter') {
		if (topUpType.value === TopUp.TopUpTypeFreeAmount && enableTopUpSubmitButton.value) {
			addTopUp();
		} else if (topUpType.value === TopUp.TopUpTypePackages && selectedWalletPackage.value) {
			addTopUpPackage();
		}
	}
};
</script>

<template>
	<ion-content
		:style="`background: linear-gradient(${primary_color}, ${primaryShade});`"
		:class="[
			{
				'payconiq': isPayconiqPreferredModeActive,
				'click-to-pay': isClickToPayPreferredModeActive,
			},
		]">
		<TopBar class="px-[34px]">
			<template #left>
				<div class="flex items-center justify-center">
					<ion-button class="w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" :class="{ 'preferred-back-button': isPreferredPaymentModeActive }" shape="round" @click="goBack">
						<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin mr-[2px]" :class="isPreferredPaymentModeActive ? 'preferred-back-icon' : 'text-black'"></ion-icon>
					</ion-button>
				</div>
			</template>

			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2" :class="{ 'text-white': isPreferredPaymentModeActive }">
					{{ t('topup.title') }}
				</div>
			</template>

			<template #right> </template>
		</TopBar>

		<div class="flex flex-col items-center w-full max-w-none h-full">
			<div v-if="tenantConfig" class="flex flex-col items-center justify-between w-full h-full pb-[120px]">
				<div class="w-full max-w-[600px] px-[34px] pb-[100px]" :class="{ 'click-to-pay-scroll-content': isClickToPayPreferredModeActive }">
					<div v-show="availablePaymentCountries.length > 1 && tenantConfig && tenantConfig!.payment_countries.length > 0" class="flex flex-col items-center justify-center">
						<div>
							<label for="paymentMethodCountry" class="text-base2 text-center block">{{ t('top_up.country_select') }}</label>
							<CountrySelect v-model="paymentMethodCountry" :availableCountries="availablePaymentCountries" />
						</div>
					</div>
					<div v-if="!encryptedRfidTag && !loginToken && !walletId && topupableWallets.length > 1" id="wallet-select-dropdown" class="relative w-full max-w-[300px] mx-auto mb-4">
						<label class="text-base2 text-center block mb-1">{{ t('top_up.wallet_select') }}</label>
						<div class="flex items-center justify-between px-4 py-2 rounded cursor-pointer text-white" @click="toggleWalletSelect">
							<span>{{ selectedWallet?.currency.prefix }} {{ selectedWallet?.currency.name }}</span>
							<ion-icon :icon="chevronDownIcon" />
						</div>
						<transition name="dropdown-fade">
							<div v-if="isWalletSelectOpen" class="absolute z-10 w-full mt-1 bg-black rounded max-h-60 overflow-auto">
								<div v-for="w in topupableWallets" :key="w.id" class="flex items-center gap-2 px-4 py-2 hover:bg-white/10 cursor-pointer text-white" @click="onWalletSelect(w.id.toString())">
									<span>{{ w.currency.prefix }} {{ w.currency.name }}</span>
								</div>
							</div>
						</transition>
					</div>
					<div class="w-full mb-2">
						<ion-segment v-if="tenantConfig.wallet_enable_packages && walletPackages && topUpType" class="h-[50px] mb-2 ion-no-margin" :value="topUpType" mode="ios">
							<ion-segment-button v-if="tenantConfig.wallet_enable_packages" class="ion-no-margin" :value="TopUp.TopUpTypePackages" @click="topUpType = TopUp.TopUpTypePackages">
								<ion-label class="font-sourcesanspro-semibold text-base2">{{ t('top_up.packages') }} </ion-label>
							</ion-segment-button>
							<ion-segment-button v-if="tenantConfig.wallet_enable_free_input" class="ion-no-margin" :value="TopUp.TopUpTypeFreeAmount" @click="topUpType = TopUp.TopUpTypeFreeAmount">
								<ion-label class="font-sourcesanspro-semibold text-base2">{{ t('top_up.free_amount') }} </ion-label>
							</ion-segment-button>
						</ion-segment>
						<!-- TODO: this message should disappear on successful topup (or successful country selection) -->
						<div class="mb-6 flex flex-col items-start justify-center p-4 font-sourcesanspro-regular rounded-md bg-white/10 small font-sourcesanspro-regular text-base2 text-white" v-if="!countrySelectionValidated && availablePaymentCountries.length > 1 && tenantConfig && tenantConfig!.payment_countries.length > 0 && !isPreferredPaymentModeActive">
							<span class="mb-1 font-extrabold">{{ t('top_up.first_country_selection_title') }}</span>
							{{ t('top_up.first_country_selection') }}
						</div>
						<div v-if="isClickToPayPreferredModeActive" class="mb-3 text-sm2 leading-[18px] font-sourcesanspro-semibold text-white">
							{{ t('top_up.click_to_pay.minimum_message') }}
						</div>
						<div v-if="userProfile && tenantConfig.top_up_general_message" v-dompurify-html="replaceTopUpGeneralMessageVariables(tenantConfig.top_up_general_message, userProfile)" class="p-4 rounded-md bg-white/5 text-white/60"></div>
					</div>

					<div class="flex p-4 items-center justify-center max-w-[600px]" v-if="!tenantConfig || !minimumTopUp">
						<ion-spinner name="dots"></ion-spinner>
					</div>

					<div v-if="topUpType === TopUp.TopUpTypeFreeAmount" class="flex flex-col items-center justify-start max-w-[600px]">
						<div v-if="tenantConfig.wallet_enable_free_input" class="flex items-start justify-center">
							<span class="font-sourcesanspro-regular text-[40px] mr-2 leading-[52px]">
								{{ fiatCurrency.prefix }}
							</span>

							<div class="mx-1">
								<CurrencyInput wrapper-class="flex flex-wrap justify-left item-center border border-solid focus-within:background-white-15" input-class="text-lg" name="amount" placeholder="0,00" step="any" @input-changed="onAmountChange" :should-validate="false" />
							</div>
						</div>

						<div class="flex flex-col items-center justify-start font-color-text-50">
							<div class="mb-2" v-if="Number(formValue.replace(',', '.')) > 0 && selectedWallet?.currency.name !== 'EUR'">
								<span class="font-sourcesanspro-regular text-l2">
									{{ fiatCurrency.prefix }} {{ formValue }} = {{ selectedWallet?.currency.prefix }}
									{{ (parseFloat(formValue.replace(',', '.')) / +selectedWallet?.currency.exchange_rate!).toFixed(2) }}
								</span>
								<span v-if="+tenantConfig.top_up_cost > 0" class="text-white/50 text-sm block text-center"> + {{ t('transaction.administrative_fee').toLowerCase() }} {{ fiatCurrency.prefix }}{{ +tenantConfig.top_up_cost }} = {{ fiatCurrency.prefix }}{{ (parseFloat(formValue.replace(',', '.')) + +tenantConfig.top_up_cost).toFixed(2) }} </span>
							</div>
							<p v-if="minimumAmountError" class="w-[249px] px-1 text-center font-sourcesanspro-regular text-base2 leading-[26px] whitespace-nowrap">
								{{ t('balance.errorMinimumAmount') }}
								<span v-if="minimumTopUp?.amount && parseFloat(formValue.replace(',', '.')) < minimumTopUp.amount"> {{ fiatCurrency.prefix }} {{ minimumTopUp.amount }} </span>
								<span v-else-if="parseFloat(formValue.replace(',', '.')) < 1"> {{ fiatCurrency.prefix }}1.00 </span>
							</p>
						</div>
					</div>

					<div v-if="minimumTopUp && topUpType === TopUp.TopUpTypePackages" class="flex flex-col w-full max-w-[600px] align-center">
						<div v-if="previousTopUp && previousTopUp.amount && minimumAmountError && checkIsLatestAmountGreaterThanMinimumAmount(minimumTopUp.amount, previousTopUp.amount)" class="flex flex-col items-center mb-8">
							<div class="mb-4">
								<span class="font-sourcesanspro-regular text-sm2">{{ t('balance.minimum_amount') }}</span>
							</div>
							<div class="flex mb-4">
								<ion-button class="rounded-full bg-white/10 small font-sourcesanspro-regular text-base2 text-white">
									<div>{{ selectedWallet?.currency.prefix }}{{ convertToCurrency(minimumTopUp.amount) }}</div>
								</ion-button>
							</div>
						</div>

						<div v-if="walletPackages">
							<ItemWithContentStandalone
								v-for="(walletPackage, index) in walletPackages"
								:key="index"
								:has-description="!!walletPackage.description"
								:icon="require('@/statics/svg/icons/box.svg')"
								:image="walletPackage.image ? walletPackage.image : undefined"
								:title="walletPackage.name"
								:price="+walletPackage.clientx_price_incl_fee > +walletPackage.clientx_price ? fiatCurrency.prefix + (+walletPackage.clientx_price).toFixed(2) + ' + ' + t('transaction.administrative_fee').toLowerCase() : fiatCurrency.prefix + walletPackage.clientx_price_incl_fee"
								:is-selected="selectedWalletPackage && selectedWalletPackage.id == walletPackage.id"
								:variant="isClickToPayPreferredModeActive ? 'click_to_pay' : 'default'"
								@on-select-item="selectPackage(walletPackage)">
								{{ walletPackage.description }}
							</ItemWithContentStandalone>
						</div>
					</div>

					<button v-if="isClickToPayPreferredModeActive" type="button" class="click-to-pay-help-row" @click="showClickToPayInfoModal = true">
						<span class="click-to-pay-help-icon">
							<span aria-hidden="true">?</span>
						</span>
						<span class="flex min-w-0 flex-col text-left leading-[18px]">
							<span class="font-sourcesanspro-bold text-sm2">{{ t('top_up.click_to_pay.first_time_title') }}</span>
							<span class="font-sourcesanspro-semibold text-xs2 text-white/90">{{ t('top_up.click_to_pay.first_time_subtitle') }}</span>
						</span>
						<ion-icon :icon="chevronForwardOutline" class="ml-auto text-white/80" />
					</button>
				</div>

				<div class="fixed bottom-0 left-0 right-0 bg-white/20 backdrop-blur z-10 px-0">
					<div class="max-w-[600px] mx-auto px-4 pt-4 pb-[env(safe-area-inset-bottom)]" :class="{ 'click-to-pay-actions-inner': isClickToPayPreferredModeActive }">
						<ion-button class="w-full max-w-none hover:bg-white hover:rounded-full mb-4" expand="full" size="large" shape="round" :disabled="(topUpType === TopUp.TopUpTypeFreeAmount && !enableTopUpSubmitButton) || (topUpType === TopUp.TopUpTypePackages && !selectedWalletPackage)" @click="topUpType === TopUp.TopUpTypeFreeAmount ? addTopUp() : addTopUpPackage()">
							<div v-if="!isPreferredPaymentModeActive" class="flex w-full items-center justify-between">
								<span>{{ t('general.continue') }}</span>
								<ion-icon :icon="arrowForwardOutline" class="text-dark-blue mr-2"></ion-icon>
							</div>
							<div v-if="isPayconiqPreferredModeActive" class="flex w-full items-center justify-between">
								<span>{{ t('balance.pay_with') }}</span>
								<img class="w-[94px] h-[31px]" :src="require('@/statics/png/payconiq/payconiq-logo-dark.png')" />
							</div>
							<div v-if="isClickToPayPreferredModeActive" class="flex w-full items-center justify-center gap-2">
								<span>{{ t('balance.pay_with') }}</span>
								<span class="mastercard-mark" aria-hidden="true"><span></span><span></span></span>
								<span class="font-sourcesanspro-black">Click to Pay</span>
							</div>
						</ion-button>
						<ion-button v-if="isPreferredPaymentModeActive" class="flex items-center justify-center mb-2 w-full font-sourcesanspro-regular text-white text-sm2 cursor-pointer" shape="round" fill="outline" @click="switchPreferredPaymentMode()">
							{{ t('top_up.other_payment_options') }}
						</ion-button>
					</div>
				</div>
			</div>
		</div>
		<ion-modal :is-open="showClickToPayInfoModal" class="click-to-pay-info-modal" @didDismiss="showClickToPayInfoModal = false">
			<div class="click-to-pay-modal">
				<div class="flex items-center justify-between mb-4">
					<h2 class="font-sourcesanspro-black text-m2 text-black">{{ t('top_up.click_to_pay.modal_title') }}</h2>
					<button type="button" class="click-to-pay-modal-close" aria-label="Close" @click="showClickToPayInfoModal = false">
						<ion-icon :icon="closeOutline" />
					</button>
				</div>

				<div class="click-to-pay-modal-callout">
					<h3>{{ t('top_up.click_to_pay.callout_title') }}</h3>
					<p>{{ t('top_up.click_to_pay.callout_body') }}</p>
				</div>

				<div class="click-to-pay-modal-list">
					<div>
						<h3>{{ t('top_up.click_to_pay.steps.setup_title') }}</h3>
						<p>{{ t('top_up.click_to_pay.steps.setup_body') }}</p>
					</div>
					<div>
						<h3>{{ t('top_up.click_to_pay.steps.storage_title') }}</h3>
						<p>{{ t('top_up.click_to_pay.steps.storage_body') }}</p>
					</div>
					<div>
						<h3>{{ t('top_up.click_to_pay.steps.other_sites_title') }}</h3>
						<p>{{ t('top_up.click_to_pay.steps.other_sites_body') }}</p>
					</div>
					<div>
						<h3>{{ t('top_up.click_to_pay.steps.remove_title') }}</h3>
						<p>{{ t('top_up.click_to_pay.steps.remove_body') }}</p>
					</div>
				</div>

				<div class="click-to-pay-modal-footer" v-dompurify-html="t('top_up.click_to_pay.powered_by')"></div>
			</div>
		</ion-modal>
	</ion-content>
</template>

<style lang="scss" scoped>
.custom-disabled {
	--background: #717171 !important;
	opacity: 1;
}

.payconiq {
	background: #ff4785 !important;
	background-color: #ff4785 !important;
}

.click-to-pay {
	background: linear-gradient(180deg, #e60024 0%, #f24a21 58%, #ffa51d 100%) !important;
	background-color: #e60024 !important;
}

.preferred-back-button {
	--background: rgba(255, 255, 255, 0.16);
	--background-activated: rgba(255, 255, 255, 0.26);
	--background-hover: rgba(255, 255, 255, 0.26);
	--box-shadow: none;
	--color: #ffffff;
}

.preferred-back-button:hover {
	background: transparent !important;
}

.preferred-back-icon {
	color: #ffffff !important;
	font-size: 20px;
}

.click-to-pay-scroll-content {
	padding-bottom: 190px !important;
}

.click-to-pay-actions-inner {
	padding-bottom: calc(env(safe-area-inset-bottom) + 16px) !important;
}

.click-to-pay-help-row {
	align-items: center;
	background: rgba(111, 74, 25, 0.44);
	border: 1px dashed rgba(255, 255, 255, 0.22);
	border-radius: 8px;
	color: white;
	display: flex;
	gap: 10px;
	margin-top: 16px;
	min-height: 58px;
	padding: 11px 12px;
	width: 100%;
}

.click-to-pay-help-icon {
	align-items: center;
	background: white;
	border-radius: 999px;
	color: #e60024;
	display: flex;
	flex: 0 0 22px;
	height: 22px;
	justify-content: center;
	width: 22px;
}

.click-to-pay-help-icon span {
	color: #e60024;
	font-size: 15px;
	font-weight: 900;
	line-height: 1;
}

.mastercard-mark {
	display: inline-flex;
	height: 18px;
	position: relative;
	width: 31px;
}

.mastercard-mark span {
	border-radius: 999px;
	display: block;
	height: 18px;
	position: absolute;
	top: 0;
	width: 18px;
}

.mastercard-mark span:first-child {
	background: #eb001b;
	left: 0;
}

.mastercard-mark span:last-child {
	background: #f79e1b;
	mix-blend-mode: multiply;
	right: 0;
}

.click-to-pay-info-modal {
	--background: #ffffff;
	--height: 100%;
	--width: 100%;
}

.click-to-pay-modal {
	background: #ffffff;
	color: #111111;
	display: flex;
	flex-direction: column;
	min-height: 100%;
	padding: calc(env(safe-area-inset-top, 47px) + 22px) 28px calc(env(safe-area-inset-bottom, 0px) + 28px);
}

.click-to-pay-modal-close {
	align-items: center;
	appearance: none;
	background: transparent;
	border: 0;
	border-radius: 999px;
	color: #5f6368;
	display: flex;
	flex: 0 0 34px;
	font-size: 24px;
	height: 34px;
	justify-content: center;
	margin: 0 0 0 12px;
	padding: 0;
	width: 34px;
}

.click-to-pay-modal-close ion-icon {
	color: #5f6368;
	display: block;
	font-size: 24px;
}

.click-to-pay-modal-callout {
	background: linear-gradient(135deg, #ec152e 0%, #ff971d 100%);
	border-radius: 9px;
	color: white;
	margin-bottom: 22px;
	padding: 16px 14px;
}

.click-to-pay-modal-callout h3 {
	font-family: var(--ion-font-family);
	font-size: 18px;
	font-weight: 900;
	line-height: 22px;
	margin: 0 0 6px;
}

.click-to-pay-modal-callout p {
	font-size: 13px;
	font-weight: 700;
	line-height: 18px;
	margin: 0;
}

.click-to-pay-modal-list {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.click-to-pay-modal-list > div {
	padding-left: 16px;
	position: relative;
}

.click-to-pay-modal-list > div::before {
	background: #e60024;
	border-radius: 999px;
	content: '';
	height: 5px;
	left: 0;
	position: absolute;
	top: 8px;
	width: 5px;
}

.click-to-pay-modal-list h3 {
	color: #111111;
	font-size: 14px;
	font-weight: 900;
	line-height: 18px;
	margin: 0 0 6px;
}

.click-to-pay-modal-list p {
	color: #6d6d6d;
	font-size: 13px;
	font-weight: 600;
	line-height: 19px;
	margin: 0;
}

.click-to-pay-modal-footer {
	border-top: 1px solid #eeeeee;
	color: #9a9a9a;
	font-size: 11px;
	font-weight: 600;
	line-height: 16px;
	margin-top: auto;
	padding-top: 18px;
	text-align: center;
}

div {
	ion-select {
		--placeholder-opacity: 1;
		height: 52px;
		width: 100%;
		justify-content: center;
	}
}

ion-segment-button {
	--indicator-color: white;

	&.segment-button-checked {
		color: black !important;
	}
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
	transition: all 0.2s ease;
}
.dropdown-fade-enter-from {
	opacity: 0;
	transform: translateY(-5px);
}
.dropdown-fade-enter-to {
	opacity: 1;
	transform: translateY(0);
}
.dropdown-fade-leave-from {
	opacity: 1;
	transform: translateY(0);
}
.dropdown-fade-leave-to {
	opacity: 0;
	transform: translateY(-5px);
}
</style>
